import { usePlaybackData } from '@/hooks/use-playback-data';
import { useAudioControls } from '@/lib/audio/audio-player-provider';
import { TokenHighlightController } from '@/lib/audio/token-highlight-controller';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Id } from '~/convex/_generated/dataModel';
import { AudioPlaybackControls } from './audio-playback-controls';
import { VirtualizedTokenRenderer } from './virtualized-token-renderer';

const SegmentDivider = () => (
  <div className="my-3 flex items-center gap-3">
    <div className="h-px flex-1 bg-gray-300" />
    <span className="text-xs whitespace-nowrap text-gray-400">Paused</span>
    <div className="h-px flex-1 bg-gray-300" />
  </div>
);

interface TranscriptionDisplayProps {
  reportId: Id<'reports'>;
  previousText?: string;
  currentSegmentText?: string;
  finalText: string;
  nonFinalText: string;
  isTranscribing?: boolean;
  isLoading?: boolean;
  tokenCount?: number | null;
  creationTime?: number;
  preview?: boolean;
  onClick?: () => void;
  hideAudioControls?: boolean;
}

export const TranscriptionDisplay = ({
  reportId,
  previousText = '',
  currentSegmentText = '',
  finalText,
  nonFinalText,
  isTranscribing = false,
  isLoading = false,
  tokenCount,
  creationTime,
  preview = false,
  onClick,
  hideAudioControls = false,
}: TranscriptionDisplayProps) => {
  const { t, i18n } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    playback,
    segmentBoundariesMs,
    tokens: remoteTokens,
    isTokensLoading,
    hasAudio,
    canHighlightTokens,
  } = usePlaybackData({ reportId, enabled: !isTranscribing });

  const highlightController = useMemo(() => new TokenHighlightController(), []);
  const { subscribeTime } = useAudioControls();

  // Reset highlight state when entering recording mode
  useEffect(() => {
    if (isTranscribing) {
      highlightController.clearAll();
    }
  }, [isTranscribing, highlightController]);

  // Subscribe highlight controller to audio time updates
  useEffect(() => {
    if (!remoteTokens || remoteTokens.length === 0) return;

    const unsubscribe = subscribeTime((timeMs) => {
      highlightController.onTimeUpdate(timeMs);
    });

    return unsubscribe;
  }, [remoteTokens, subscribeTime, highlightController]);

  const transcriptionStats = useMemo(() => {
    const textToAnalyze = finalText || nonFinalText;
    if (!textToAnalyze) return null;

    const wordCount = textToAnalyze.trim().split(/\s+/).filter(Boolean).length;
    const characterCount = textToAnalyze.length;

    const savedDate = creationTime
      ? new Date(creationTime).toLocaleDateString(i18n.language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : null;

    return { wordCount, characterCount, tokenCount, savedDate };
  }, [finalText, nonFinalText, tokenCount, creationTime, i18n.language]);

  const showAudioControls = hasAudio && !isTranscribing;
  const showTokenMode = showAudioControls && canHighlightTokens;
  const isPlaybackLoading = isLoading || isTokensLoading;

  const segmentBreakIndices = useMemo(() => {
    if (!remoteTokens || segmentBoundariesMs.length === 0)
      return new Set<number>();
    const indices = new Set<number>();
    for (const boundaryMs of segmentBoundariesMs) {
      const idx = remoteTokens.findIndex((t) => t.startMs >= boundaryMs);
      if (idx > 0) indices.add(idx);
    }
    return indices;
  }, [remoteTokens, segmentBoundariesMs]);

  // Auto-scroll to bottom during live transcription
  useEffect(() => {
    if (!isTranscribing) return;
    const el = contentRef.current;
    if (!el) return;

    const observer = new MutationObserver(() => {
      const isNearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      if (isNearBottom) {
        el.scrollTop = el.scrollHeight;
      }
    });

    observer.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    return () => observer.disconnect();
  }, [isTranscribing]);

  const hasLiveSegmentBreak =
    isTranscribing &&
    previousText.length > 0 &&
    (currentSegmentText.length > 0 || nonFinalText.length > 0);

  const transcriptionText = useMemo(() => {
    if (showTokenMode && remoteTokens) {
      return remoteTokens.map((token) => token.text).join('');
    }
    if (isTranscribing) {
      return finalText + (nonFinalText || '');
    }
    return playback?.rawTranscription || finalText || '';
  }, [
    showTokenMode,
    remoteTokens,
    isTranscribing,
    finalText,
    nonFinalText,
    playback?.rawTranscription,
  ]);

  // Preview mode
  if (preview) {
    return (
      <div className="flex w-full flex-col gap-3">
        <div
          onClick={onClick}
          className={cn(
            'relative w-full rounded-xl border bg-gray-100 px-4 py-3 transition-colors',
            onClick && 'cursor-pointer hover:bg-gray-200',
          )}>
          <div className="flex flex-row items-center gap-2 pb-2">
            <p className="text-sm font-light text-gray-500 uppercase">
              {t('home:transcription')}
            </p>
            {isPlaybackLoading ? (
              <Loader2 className="size-2 animate-spin" />
            ) : (
              <div
                className={cn(
                  'size-2 rounded-full bg-red-500 transition-all duration-700',
                  isTranscribing ? 'animate-pulse opacity-100' : 'opacity-0',
                )}
              />
            )}
          </div>

          <div className="line-clamp-4 leading-relaxed text-gray-900">
            {transcriptionText || (
              <span className="text-gray-400">
                {t('home:transcriptionEmpty')}
              </span>
            )}
          </div>

          {onClick && transcriptionText && (
            <div className="text-primary mt-2 text-xs">
              View full transcription →
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full mode
  return (
    <div className="relative h-full flex-1">
      <div
        ref={scrollRef}
        className="absolute inset-0 flex flex-col rounded-xl">
        <div className="flex flex-row items-center gap-2 pb-1">
          <p className="text-xs font-light text-gray-500 uppercase">
            {t('home:transcription')}
          </p>
          {isPlaybackLoading ? (
            <Loader2 className="size-2 animate-spin" />
          ) : (
            <div
              className={cn(
                'size-2 rounded-full bg-red-500 transition-all duration-700',
                isTranscribing ? 'animate-pulse opacity-100' : 'opacity-0',
              )}
            />
          )}
        </div>

        {transcriptionStats && (
          <div className="flex flex-wrap items-center gap-3 pt-1 pb-2 text-xs text-gray-600">
            {transcriptionStats.savedDate && (
              <>
                <span className="font-medium">
                  {transcriptionStats.savedDate}
                </span>
                <span className="text-gray-400">•</span>
              </>
            )}
            <span className="font-medium">
              {transcriptionStats.wordCount.toLocaleString()}{' '}
              {transcriptionStats.wordCount === 1
                ? t('home:stats.word')
                : t('home:stats.words')}
            </span>
            <span className="text-gray-400">•</span>
            <span>
              {transcriptionStats.characterCount.toLocaleString()}{' '}
              {transcriptionStats.characterCount === 1
                ? t('home:stats.character')
                : t('home:stats.characters')}
            </span>
            {transcriptionStats.tokenCount != null && (
              <>
                <span className="text-gray-400">•</span>
                <span>
                  {transcriptionStats.tokenCount.toLocaleString()}{' '}
                  {transcriptionStats.tokenCount === 1
                    ? t('home:stats.token')
                    : t('home:stats.tokens')}
                </span>
              </>
            )}
          </div>
        )}

        {showAudioControls && !hideAudioControls && (
          <AudioPlaybackControls segmentBoundariesMs={segmentBoundariesMs} />
        )}

        <div
          ref={contentRef}
          className="scrollbar-none relative flex-1 overflow-y-auto pb-20 text-lg leading-relaxed">
          {transcriptionText ? (
            showTokenMode ? (
              <VirtualizedTokenRenderer
                tokens={remoteTokens ?? []}
                highlightController={highlightController}
                segmentBreakIndices={segmentBreakIndices}
                rootRef={scrollRef}
                debug={false}
              />
            ) : isTranscribing ? (
              <>
                {previousText && <span>{previousText}</span>}
                {hasLiveSegmentBreak && <SegmentDivider />}
                {currentSegmentText && <span>{currentSegmentText}</span>}
                {nonFinalText && (
                  <span className="text-gray-500">{nonFinalText}</span>
                )}
              </>
            ) : (
              <span>{playback?.rawTranscription}</span>
            )
          ) : !isPlaybackLoading ? (
            <span className="text-gray-400">
              {t('home:transcriptionEmpty')}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};
